import { useEffect, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Link, useLocation } from "react-router-dom";
import { Toaster } from "sonner";

import { AuthProvider, CartProvider, WishlistProvider, useAuth } from "@/lib/store";
import { api } from "@/lib/api";
import { CookieBanner } from "@/components/CookieBanner";
import { ExpiringVoucherBanner } from "@/components/ExpiringVoucherBanner";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { CartDrawer } from "@/components/CartDrawer";
import { AuthModal } from "@/components/AuthModal";

import Home from "@/pages/Home";
import Catalog from "@/pages/Catalog";
import ProductDetail from "@/pages/ProductDetail";
import Checkout from "@/pages/Checkout";
import Account from "@/pages/Account";
import Community from "@/pages/Community";
import ProductRequest from "@/pages/ProductRequest";
import Admin from "@/pages/Admin";
import AuthCallback from "@/pages/AuthCallback";
import ChiSiamo from "@/pages/ChiSiamo";
import Spedizioni from "@/pages/Spedizioni";
import ComingSoon from "@/pages/ComingSoon";
import { PrivacyPolicy, CookiePolicy, TermsPage } from "@/pages/LegalPage";

const PUBLIC_PATHS = ["/privacy", "/cookie", "/termini"];

const toaster = (
  <Toaster
    theme="dark"
    position="bottom-right"
    toastOptions={{
      style: { background: "#0b0b0d", border: "1px solid rgba(201,162,39,0.3)", color: "#fff" },
    }}
  />
);

function Shell() {
  const [authOpen, setAuthOpen] = useState(false);
  const location = useLocation();
  const { user, loading } = useAuth();
  const [comingSoon, setComingSoon] = useState(null);

  useEffect(() => {
    api.get("/settings/public").then(({ data }) => setComingSoon(data.coming_soon)).catch(() => setComingSoon(false));
    const onChange = (e) => setComingSoon(e.detail);
    window.addEventListener("ps-coming-soon", onChange);
    return () => window.removeEventListener("ps-coming-soon", onChange);
  }, []);

  // Emergent OAuth callback detection (fragment)
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }

  if (comingSoon === null || loading) return <div className="min-h-screen bg-[#090909]" />;

  const isAdmin = !!user?.is_admin;
  if (comingSoon && !isAdmin && PUBLIC_PATHS.includes(location.pathname)) {
    return (
      <div className="App min-h-screen bg-[#090909]">
        <div className="max-w-3xl mx-auto px-4 pt-8">
          <Link to="/" data-testid="legal-back-home" className="text-xs font-mono-ps uppercase tracking-widest text-[#C9A227] hover:text-white transition-colors">← PULL STATION</Link>
        </div>
        <Routes>
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/cookie" element={<CookiePolicy />} />
          <Route path="/termini" element={<TermsPage />} />
        </Routes>
        {toaster}
      </div>
    );
  }
  if (comingSoon && !isAdmin) {
    return (
      <>
        <ComingSoon onOpenAuth={() => setAuthOpen(true)} />
        <AuthModal open={authOpen} onClose={() => setAuthOpen(false)} />
        <CookieBanner />
        {toaster}
      </>
    );
  }

  return (
    <div className="App min-h-screen flex flex-col">
      {comingSoon && isAdmin && (
        <div data-testid="coming-soon-admin-bar" className="bg-[#C9A227] text-black text-center text-xs font-mono-ps uppercase tracking-widest py-1.5">
          Modalità Coming Soon attiva — solo tu vedi il sito completo
        </div>
      )}
      <ExpiringVoucherBanner />
      <Header onOpenAuth={() => setAuthOpen(true)} />
      <main className="flex-1">
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/prodotto/:slug" element={<ProductDetail />} />
          <Route path="/checkout" element={<Checkout />} />
          <Route path="/account" element={<Account />} />
          <Route path="/community" element={<Community />} />
          <Route path="/richieste" element={<ProductRequest />} />
          <Route path="/chi-siamo" element={<ChiSiamo />} />
          <Route path="/spedizioni" element={<Spedizioni />} />
          <Route path="/admin" element={<Admin />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/cookie" element={<CookiePolicy />} />
          <Route path="/termini" element={<TermsPage />} />
          <Route path="/search" element={<Catalog />} />
          <Route path="/:category" element={<Catalog />} />
        </Routes>
      </main>
      <Footer />
      <CartDrawer />
      <AuthModal open={authOpen} onClose={() => setAuthOpen(false)} />
      <CookieBanner />
      {toaster}
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <WishlistProvider>
        <CartProvider>
          <BrowserRouter>
            <Shell />
          </BrowserRouter>
        </CartProvider>
      </WishlistProvider>
    </AuthProvider>
  );
}

export default App;
